import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import { Box, ToggleButton, ToggleButtonGroup, Typography, CircularProgress } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LockIcon from '@mui/icons-material/Lock';
import { useAuthTenant } from 'store/auth';
import { Container } from 'Components/Container';
import { getPreviewPublic, getPreviewProtected } from 'services/transparency-service';
import { PreviewBanner } from './PreviewBanner';

// Import components from Public and Protected views
import { TransparencyHeader } from '../Public/components/TransparencyHeader';
import { FrameworkBadges } from '../Public/components/FrameworkBadges';
import { GovernanceOverview } from '../Public/components/GovernanceOverview';
import { TrendIndicator } from '../Public/components/TrendIndicator';
import { PublicPolicies } from '../Public/components/PublicPolicies';
import { OverviewTab } from '../Protected/components/OverviewTab';
import { FrameworksTab } from '../Protected/components/FrameworksTab';
import { GovernanceStructure } from '../Protected/components/GovernanceStructure';
import { PoliciesTab } from '../Protected/components/PoliciesTab';
import {
  TransparencyPageContainer,
  TransparencyPageContent,
  SectionTitle,
} from '../Public/styles';

/**
 * TransparencyPreview - Preview Mode (Story-009)
 * - Internal users only (JWT required)
 * - Simulates public and protected views
 * - Does not generate audit logs
 * - Shows "Preview Mode" banner
 */
export const TransparencyPreview = () => {
  const [viewMode, setViewMode] = useState('public');
  const selectedTenant = useSelector(useAuthTenant);
  const companyKey = selectedTenant?.slug;

  return (
    <Container
      title="Centro de Transparência - Preview"
      breadcrumb="Configurações / Centro de Transparência / Preview"
    >
      {/* Preview Mode Banner */}
      <PreviewBanner />

      {/* View Mode Toggle */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Selecione o modo de visualização:
        </Typography>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => v && setViewMode(v)}
          size="small"
        >
          <ToggleButton value="public" sx={{ px: 3 }}>
            <VisibilityIcon sx={{ mr: 1, fontSize: 18 }} />
            Visão Pública
          </ToggleButton>
          <ToggleButton value="protected" sx={{ px: 3 }}>
            <LockIcon sx={{ mr: 1, fontSize: 18 }} />
            Visão Protegida
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Simulated View */}
      <Box
        sx={{
          border: '2px dashed #1976d2',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: '#f5f7fa',
        }}
      >
        {viewMode === 'public' ? (
          <PublicPreview companyKey={companyKey} />
        ) : (
          <ProtectedPreview companyKey={companyKey} />
        )}
      </Box>
    </Container>
  );
};

/**
 * PublicPreview - Preview of public transparency view (uses preview API)
 */
const PublicPreview = ({ companyKey }) => {
  const { isLoading, error, data } = useQuery(
    ['previewPublic', companyKey],
    () => getPreviewPublic(companyKey),
    { enabled: !!companyKey }
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data?.data) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          Não foi possível carregar o preview. Verifique se o centro de transparência está habilitado.
        </Typography>
      </Box>
    );
  }

  const { organization, frameworkBadges, trendIndicator, governanceStructure, policies } = data.data;

  return (
    <TransparencyPageContainer>
      <TransparencyHeader
        name={organization?.name}
        description={organization?.description}
        lastUpdated={organization?.lastUpdated}
      />

      <TransparencyPageContent>
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: 2 }}>
          {trendIndicator && (
            <Box sx={{ mb: 4 }}>
              <TrendIndicator
                label={trendIndicator.label}
                description={trendIndicator.description}
                trend={trendIndicator.trend}
              />
            </Box>
          )}

          {frameworkBadges && frameworkBadges.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <SectionTitle>Nossos Compromissos</SectionTitle>
              <FrameworkBadges badges={frameworkBadges} />
            </Box>
          )}

          {governanceStructure && (
            <Box sx={{ mb: 4 }}>
              <SectionTitle>Estrutura de Governança</SectionTitle>
              <GovernanceOverview
                categories={governanceStructure.categories}
                overallPosture={governanceStructure.overallPosture}
              />
            </Box>
          )}

          {policies && policies.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <SectionTitle>Políticas Públicas</SectionTitle>
              <PublicPolicies policies={policies} />
            </Box>
          )}
        </Box>
      </TransparencyPageContent>
    </TransparencyPageContainer>
  );
};

/**
 * ProtectedPreview - Preview of protected transparency view (uses preview API)
 */
const ProtectedPreview = ({ companyKey }) => {
  const [activeTab, setActiveTab] = useState(0);

  const { isLoading, error, data } = useQuery(
    ['previewProtected', companyKey],
    () => getPreviewProtected(companyKey),
    { enabled: !!companyKey }
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data?.data) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          Não foi possível carregar o preview. Verifique se o centro de transparência está habilitado.
        </Typography>
      </Box>
    );
  }

  const { organization, frameworks, governanceStructure, policies } = data.data;

  return (
    <Box sx={{ p: 4, bgcolor: 'white' }}>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        Centro de Transparência - {organization?.name}
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <ToggleButtonGroup
          value={activeTab}
          exclusive
          onChange={(_, v) => v !== null && setActiveTab(v)}
          size="small"
        >
          <ToggleButton value={0}>Visão Geral</ToggleButton>
          <ToggleButton value={1}>Frameworks</ToggleButton>
          <ToggleButton value={2}>Governança</ToggleButton>
          <ToggleButton value={3}>Políticas</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ pt: 2 }}>
        {activeTab === 0 && (
          <OverviewTab
            organization={organization}
            frameworks={frameworks}
            governanceStructure={governanceStructure}
          />
        )}
        {activeTab === 1 && (
          <FrameworksTab
            frameworks={frameworks}
            companyKey={companyKey}
            accessToken={null}
          />
        )}
        {activeTab === 2 && (
          <GovernanceStructure
            categories={governanceStructure?.categories}
            overallPosture={governanceStructure?.overallPosture}
            showSubDomains={true}
          />
        )}
        {activeTab === 3 && (
          <PoliciesTab
            policies={policies}
            companyKey={companyKey}
            accessToken={null}
          />
        )}
      </Box>
    </Box>
  );
};

export { PreviewBanner };
